/**
 * SlideExportDialog
 *
 * Main dialog for exporting plays and documents to PPTX/PDF.
 * Provides content selection, layout configuration, metadata toggles,
 * and a thumbnail preview grid before export.
 */

import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from '../ui/Dialog'
import { Button } from '../ui/Button'
import type { ExportSection as Section } from '@/components/playbook-editor/Types'
import { useSlideExport } from '@/hooks/UseSlideExport'
import { ContentSelector } from './ContentSelector'
import { LayoutPicker } from './LayoutPicker'
import { MetadataToggles } from './MetadataToggles'
import { SlideOrganizer } from './SlideOrganizer'
import './slide-export-dialog.css'

interface SlideExportDialogProps {
	isOpen: boolean
	onClose: () => void
	sections: Section[]
	playbookId: string
	playbookName: string
}

export function SlideExportDialog({
	isOpen,
	onClose,
	sections,
	playbookId,
	playbookName,
}: SlideExportDialogProps) {
	const exportState = useSlideExport(playbookId, playbookName, sections)

	const {
		selectedSectionIds,
		toggleSection,
		selectAllSections,
		deselectAllSections,
		layout,
		setLayout,
		format,
		setFormat,
		metadata,
		toggleMetadata,
		includeDividers,
		setIncludeDividers,
		slides,
		selectedPlayCount,
		isCustomized,
		movePlay,
		resetSlides,
		isExporting,
		exportError,
		exportSlides,
	} = exportState

	const handleExport = async () => {
		const success = await exportSlides()
		if (success) {
			onClose()
		}
	}

	const slideCount = slides.filter((s) => s.type === 'content').length
	const formatLabel = format.toUpperCase()

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="slide-export-dialog">
				<DialogHeader>
					<DialogTitle>Export Slides</DialogTitle>
					<DialogDescription>
						Select plays and configure your {formatLabel} export
					</DialogDescription>
				</DialogHeader>

				<div className="slide-export-dialog__body">
					{/* Content Selection */}
					<ContentSelector
						sections={sections}
						selectedSectionIds={selectedSectionIds}
						onToggleSection={toggleSection}
						onSelectAll={selectAllSections}
						onDeselectAll={deselectAllSections}
					/>

					{/* Layout + Format + Metadata */}
					<div className="slide-export-dialog__config">
						<LayoutPicker
							layout={layout}
							onLayoutChange={setLayout}
						/>

						<MetadataToggles
							metadata={metadata}
							onToggle={toggleMetadata}
							includeDividers={includeDividers}
							onToggleDividers={setIncludeDividers}
						/>

						<div className="slide-export-dialog__format">
							<span className="slide-export-dialog__label">Format</span>
							<div className="slide-export-dialog__format-options">
								<button
									className="slide-export-dialog__format-btn"
									data-selected={format === 'pptx'}
									onClick={() => setFormat('pptx')}
								>
									PowerPoint
								</button>
								<button
									className="slide-export-dialog__format-btn"
									data-selected={format === 'pdf'}
									onClick={() => setFormat('pdf')}
								>
									PDF
								</button>
							</div>
						</div>
					</div>

					{/* Slide Organizer */}
					{slides.length > 0 && (
						<SlideOrganizer
							slides={slides}
							sections={sections}
							selectedSectionIds={selectedSectionIds}
							isCustomized={isCustomized}
							onMovePlay={movePlay}
							onReset={resetSlides}
						/>
					)}

					{exportError && (
						<div className="slide-export-dialog__error">
							{exportError}
						</div>
					)}
				</div>

				<DialogFooter>
					<span className="slide-export-dialog__summary">
						{selectedPlayCount} plays, {slideCount} slides
					</span>
					<Button variant="outline" onClick={onClose} disabled={isExporting}>
						Cancel
					</Button>
					<Button
						variant="action"
						onClick={handleExport}
						disabled={isExporting || selectedPlayCount === 0}
					>
						{isExporting ? 'Exporting...' : `Export ${formatLabel}`}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
